import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AlertCircle,
  ArrowUpRight,
  Bot,
  Check,
  CheckCircle,
  Code2,
  Copy,
  Database,
  Github,
  Linkedin,
  Loader2,
  Mail,
  MapPin,
  Send,
  Twitter,
  Workflow,
} from "lucide-react";
import { Container } from "../components/layout/container";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";
import { trackContact, trackFormSubmission } from "../utils/analytics";
import { useLanguage } from "../context/LanguageContext";

const EMAIL_LOCAL = "nandanpriyadarshi298";
const EMAIL_DOMAIN = "@gmail.com";
const EMAIL = `${EMAIL_LOCAL}${EMAIL_DOMAIN}`;

const contactTopics = [
  { label: "AI product", subject: "AI product strategy", icon: Bot },
  { label: "Data platform", subject: "Data product or platform", icon: Database },
  { label: "Embedded delivery", subject: "Forward-deployed collaboration", icon: Workflow },
  { label: "Solo build", subject: "Product build collaboration", icon: Code2 },
];

const briefPrompts = [
  { question: "Who is blocked?", detail: "The user, team, or customer in the workflow." },
  { question: "What breaks today?", detail: "The handoff, data constraint, or decision slowing them down." },
  { question: "What should change?", detail: "The outcome that would make the product worth shipping." },
];

const socialLinks = [
  { icon: Github, href: "https://github.com/nandanosql/", label: "GitHub" },
  { icon: Linkedin, href: "https://www.linkedin.com/in/nandan-priyadarshi/", label: "LinkedIn" },
  { icon: Twitter, href: "https://x.com/nandanpri", label: "X" },
];

export default function ContactPage() {
  const { strings } = useLanguage();
  const c = strings?.contact;
  const seo = c?.seo;

  const currentContactTopics = contactTopics.map((topic, index) => ({
    ...topic,
    label: c?.topics?.[index]?.label || topic.label,
    subject: c?.topics?.[index]?.subject || topic.subject,
  }));

  const currentBriefPrompts = briefPrompts.map((prompt, index) => ({
    question: c?.briefPrompts?.[index]?.question || prompt.question,
    detail: c?.briefPrompts?.[index]?.detail || prompt.detail,
  }));

  const [formState, setFormState] = useState("idle");
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleTopicSelect = (index) => {
    const topic = currentContactTopics[index];
    setSelectedTopic(index);
    setFormData((previous) => ({ ...previous, subject: topic.subject }));
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const accessKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

    if (!accessKey) {
      setFormState("error");
      trackFormSubmission("Contact Form", false);
      return;
    }

    setFormState("submitting");

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: accessKey,
          name: formData.name,
          email: formData.email,
          subject: `Portfolio Contact: ${formData.subject}`,
          message: formData.message,
          from_name: formData.name,
          to_email: EMAIL,
          redirect: "false",
        }),
      });

      const result = await response.json();

      if (!result.success) {
        console.error("Form submission error:", result);
        setFormState("error");
        trackFormSubmission("Contact Form", false);
        return;
      }

      setFormState("success");
      setFormData({ name: "", email: "", subject: "", message: "" });
      setSelectedTopic(null);
      trackFormSubmission("Contact Form", true);
      trackContact("Contact Form");
    } catch (error) {
      console.error("Form error:", error);
      setFormState("error");
      trackFormSubmission("Contact Form", false);
    }
  };

  const resetForm = () => setFormState("idle");

  const entryMotion = reduceMotion
    ? { initial: false, animate: { opacity: 1 }, transition: { duration: 0.1 } }
    : {
        initial: { opacity: 0, transform: "translateY(12px)" },
        animate: { opacity: 1, transform: "translateY(0)" },
        transition: { duration: 0.45, ease: [0.23, 1, 0.32, 1] },
      };

  return (
    <div className="min-h-[100dvh] bg-[#0B0B0F] text-white">
      <SEO
        title={seo?.title || "Contact Nandan Priyadarshi | Data and AI Product Collaboration"}
        description={seo?.description || "Discuss an AI product, data platform, forward-deployed engagement, or product build with Nandan Priyadarshi."}
        canonicalUrl="/contact-me"
        keywords={seo?.keywords || "Contact Nandan Priyadarshi, Data Product Manager, AI Product Manager, Forward Deployed Product Builder"}
      />

      <main className="relative overflow-hidden py-12 sm:py-16 lg:py-20">
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,_rgba(138,180,255,0.11),_transparent_28%)]"
          aria-hidden="true"
        />
        <Container className="relative grid items-start gap-12 lg:grid-cols-[minmax(0,0.82fr)_minmax(520px,1.18fr)] lg:gap-16">
          <motion.section className="lg:sticky lg:top-28" aria-labelledby="contact-title" {...entryMotion}>
            <p className="text-sm font-medium text-[#8AB4FF]">{c?.eyebrow || "Open a channel"}</p>
            <h1
              id="contact-title"
              className="mt-4 max-w-xl text-4xl font-semibold tracking-[-0.045em] text-white sm:text-5xl lg:text-[3.5rem] lg:leading-[1.03]"
            >
              {c?.title || "Start with the hard part."}
            </h1>
            <p className="mt-6 max-w-lg text-base leading-7 text-white/65 sm:text-lg">
              {c?.subtitle || "Share the workflow, data constraint, or AI product decision. I’ll meet you at the problem."}
            </p>

            <div className="mt-9 border-y border-white/10 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#8AB4FF]/25 bg-[#8AB4FF]/10 text-[#B7D0FF]">
                  <Mail className="h-5 w-5" aria-hidden="true" />
                </div>
                <p className="min-w-0 flex-1 text-xs font-semibold uppercase tracking-[0.15em] text-white/35">
                  {c?.directEmail || "Direct email"}
                </p>
                <button
                  type="button"
                  onClick={handleCopyEmail}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.035] text-white/60 transition-[transform,background-color,border-color,color] duration-150 hover:border-white/20 hover:bg-white/[0.07] hover:text-white active:scale-[0.97]"
                  aria-label={copied ? (c?.copied || "Email copied") : (c?.copyEmail || "Copy email address")}
                >
                  {copied ? <Check className="h-4 w-4" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
                </button>
              </div>
              <a
                href={`mailto:${EMAIL}`}
                className="mt-4 block text-sm font-semibold text-white hover:text-[#B7D0FF]"
                aria-label={`Email ${EMAIL}`}
              >
                <span>{EMAIL_LOCAL}</span>
                <span className="block sm:inline">{EMAIL_DOMAIN}</span>
              </a>
            </div>

            <div className="mt-9">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-white/35">
                {c?.usefulFirstMsg || "A useful first message answers"}
              </p>
              <div className="mt-4 border-t border-white/10">
                {currentBriefPrompts.map((prompt) => (
                  <div key={prompt.question} className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-4 border-b border-white/10 py-4">
                    <p className="text-sm font-semibold text-white/80">{prompt.question}</p>
                    <p className="text-sm leading-6 text-white/45">{prompt.detail}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 text-sm text-white/45">
                <MapPin className="h-4 w-4 text-[#8AB4FF]" aria-hidden="true" />
                {c?.location || "Beijing, China"}
              </span>
              <span className="h-4 w-px bg-white/10" aria-hidden="true" />
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-white/45 transition-[transform,background-color,border-color,color] duration-150 hover:border-white/20 hover:bg-white/[0.07] hover:text-white active:scale-[0.97]"
                  aria-label={social.label}
                >
                  <social.icon className="h-4 w-4" aria-hidden="true" />
                </a>
              ))}
            </div>
          </motion.section>

          <motion.section
            className="overflow-hidden rounded-[32px] border border-white/10 bg-[#12121A]/95 shadow-2xl shadow-black/25"
            aria-labelledby="working-brief-title"
            aria-live="polite"
            {...entryMotion}
            transition={{ ...entryMotion.transition, delay: reduceMotion ? 0 : 0.08 }}
          >
            <AnimatePresence mode="wait">
              {formState === "success" ? (
                <motion.div
                  key="success"
                  className="flex min-h-[680px] flex-col items-center justify-center px-6 py-16 text-center sm:px-10"
                  initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: reduceMotion ? 0.1 : 0.25, ease: [0.23, 1, 0.32, 1] }}
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-[#8AB4FF]/30 bg-[#8AB4FF]/10 text-[#B7D0FF]">
                    <CheckCircle className="h-8 w-8" aria-hidden="true" />
                  </div>
                  <h2 className="mt-6 text-3xl font-semibold tracking-tight text-white">
                    {c?.sentTitle || "Message sent"}
                  </h2>
                  <p className="mt-3 max-w-sm text-base leading-7 text-white/55">
                    {c?.sentDesc || "Thanks for the context. I’ll review the problem and get back to you."}
                  </p>
                  <Button variant="secondary" onClick={resetForm} className="mt-8">
                    {c?.sendAnother || "Send another brief"}
                  </Button>
                </motion.div>
              ) : formState === "error" ? (
                <motion.div
                  key="error"
                  className="flex min-h-[680px] flex-col items-center justify-center px-6 py-16 text-center sm:px-10"
                  initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-red-400/20 bg-red-400/10 text-red-300">
                    <AlertCircle className="h-8 w-8" aria-hidden="true" />
                  </div>
                  <h2 className="mt-6 text-3xl font-semibold tracking-tight text-white">
                    {c?.errorTitle || "Something went wrong"}
                  </h2>
                  <p className="mt-3 max-w-sm text-base leading-7 text-white/55">
                    {c?.errorDesc || "Try again, or use the direct email channel instead."}
                  </p>
                  <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <Button variant="secondary" onClick={resetForm}>
                      {c?.tryAgain || "Try again"}
                    </Button>
                    <Button asChild>
                      <a href={`mailto:${EMAIL}`}>
                        {c?.emailDirectly || "Email directly"}
                      </a>
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="border-b border-white/10 px-6 py-7 sm:px-8 sm:py-8">
                    <div className="flex items-start justify-between gap-5">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#8AB4FF]">
                          {c?.workingBrief || "Working brief"}
                        </p>
                        <h2 id="working-brief-title" className="mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                          {c?.workingBriefTitle || "Tell me where the workflow breaks."}
                        </h2>
                        <p className="mt-3 text-sm leading-6 text-white/50">
                          {c?.workingBriefSub || "A rough brief is enough. I’ll ask the useful questions."}
                        </p>
                      </div>
                      <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-white/25" aria-hidden="true" />
                    </div>
                  </div>

                  <form className="space-y-7 px-6 py-7 sm:px-8 sm:py-8" onSubmit={handleSubmit}>
                    <fieldset>
                      <legend className="text-sm font-medium text-white/70">
                        {c?.topicLegend || "What are we discussing?"}
                      </legend>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {currentContactTopics.map((topic, index) => {
                          const Icon = topic.icon;
                          const isSelected = selectedTopic === index;
                          return (
                            <button
                              key={topic.label}
                              type="button"
                              onClick={() => handleTopicSelect(index)}
                              className={`flex min-h-12 items-center gap-2 rounded-xl border px-3 py-2 text-left text-xs font-medium transition-[transform,background-color,border-color,color] duration-150 active:scale-[0.98] sm:text-sm ${
                                isSelected
                                  ? "border-[#8AB4FF]/45 bg-[#8AB4FF]/10 text-[#B7D0FF]"
                                  : "border-white/10 bg-white/[0.025] text-white/55 hover:border-white/20 hover:bg-white/[0.05] hover:text-white"
                              }`}
                              aria-pressed={isSelected}
                            >
                              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                              {topic.label}
                            </button>
                          );
                        })}
                      </div>
                    </fieldset>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-white/70">
                          {c?.nameLabel || "Your name"}
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3 text-white placeholder:text-white/30 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#8AB4FF]/50"
                          placeholder={c?.namePlaceholder || "Your name"}
                          required
                          disabled={formState === "submitting"}
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-white/70">
                          {c?.emailLabel || "Your email"}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3 text-white placeholder:text-white/30 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#8AB4FF]/50"
                          placeholder={c?.emailPlaceholder || "you@example.com"}
                          required
                          disabled={formState === "submitting"}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-white/70">
                        {c?.subjectLabel || "Subject"}
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="mt-2 w-full rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3 text-white placeholder:text-white/30 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#8AB4FF]/50"
                        placeholder={c?.subjectPlaceholder || "What should we unpack?"}
                        required
                        disabled={formState === "submitting"}
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-white/70">
                        {c?.messageLabel || "The working context"}
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={6}
                        className="mt-2 w-full resize-none rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3 text-white placeholder:text-white/30 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#8AB4FF]/50"
                        placeholder={c?.messagePlaceholder || "Who is blocked, what breaks today, and what should change?"}
                        required
                        disabled={formState === "submitting"}
                      />
                    </div>

                    <div className="flex flex-col gap-4 border-t border-white/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
                      <p className="max-w-xs text-xs leading-5 text-white/35">
                        {c?.privacyNote || "Your message goes directly to my inbox. No newsletter or automated sequence."}
                      </p>
                      <Button type="submit" size="lg" disabled={formState === "submitting"}>
                        {formState === "submitting" ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                            {c?.sending || "Sending"}
                          </>
                        ) : (
                          <>
                            {c?.sendBrief || "Send brief"}
                            <Send className="h-4 w-4" aria-hidden="true" />
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.section>
        </Container>
      </main>
    </div>
  );
}
